import { jwtDecode } from 'jwt-decode';

import { DecodedToken, DefaultResponse } from '../shared.types';

import { PasswordLoginData, ForgotPasswordData } from './auth.types';

import axiosServer from '@/services/axios';
import { useAuthStore } from '@/stores/useAuthStore';
import { transformToApiError } from '@/utils/error.ts';

export const passwordLogin = async (
  emailLoginData: PasswordLoginData
): Promise<DefaultResponse> => {
  try {
    const reqData = {
      identifier: emailLoginData.email,
      password: emailLoginData.password,
    };
    const res = await axiosServer.post('/signin', reqData, {
      withCredentials: true,
    });

    if (!res.data.success) {
      return res.data;
    }

    const { token } = res.headers;
    const decoded = jwtDecode<DecodedToken>(token);

    if (decoded.role !== 'admin' && decoded.role !== 'corporate') {
      return {
        success: false,
        error: {
          code: 403,
          message: 'Access denied: You do not have the required role',
        },
        content: null,
      };
    }

    const { firstName, lastName } = res.data.content;

    const auth = useAuthStore.getState();
    auth.login(token, firstName, lastName, decoded.role);

    return res.data;
  } catch (error) {
    return transformToApiError(error);
  }
};

export const changePassword = async (
  password: string
): Promise<DefaultResponse> => {
  try {
    const res = await axiosServer.post(
      '/signin/change-password',
      { password },
      {
        withCredentials: true,
      }
    );

    return res.data;
  } catch (error) {
    return transformToApiError(error);
  }
};

export const resetPassword = async (
  password: string
): Promise<DefaultResponse> => {
  try {
    const res = await axiosServer.post(
      '/signin/reset-password',
      { password },
      {
        withCredentials: true,
      }
    );

    return res.data;
  } catch (error) {
    return transformToApiError(error);
  }
};

export const forgotPassword = async (
  reqData: ForgotPasswordData
): Promise<DefaultResponse> => {
  try {
    const res = await axiosServer.post('/signin/forgot-password', reqData, {
      withCredentials: true,
    });

    if (!res.data.success) {
      return res.data;
    }

    return res.data;
  } catch (error) {
    return transformToApiError(error);
  }
};

export const sendLogout = async (): Promise<DefaultResponse> => {
  try {
    const res = await axiosServer.get('/signout', {
      withCredentials: true,
    });

    const { logout } = useAuthStore.getState();
    logout();

    return res.data;
  } catch (error) {
    return transformToApiError(error);
  }
};
